import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, Table } from "react-bootstrap";
import { Sparkles } from "lucide-react";
import Axios, { authHeaders } from "../../utils/axios";
import { useToast } from "../../context/TostContext";

/**
 * Sharpening one already-georeferenced PDF sheet: the check (a dry run that
 * changes nothing) and, if it passes, applying it. The backend does both; see
 * the R-Point backend's georeference/sharpen.service.js.
 */

const GROUP_LABELS = {
  A: "PDF georeferenced without a crop",
  B: "PDF cropped the old way (crop recovered from the saved cut-out)",
  C: "Image scan: already at full resolution",
  done: "Already sharp",
};

const VERDICTS = {
  ok: { text: "Can be sharpened", className: "text-success" },
  review: { text: "Can't be sharpened automatically: re-crop it in the georeference screen", className: "text-warning" },
  skip: { text: "Nothing to do", className: "text-secondary" },
  error: { text: "The check failed", className: "text-danger" },
};

const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : "-");

export default function SharpenModal({ row, onHide, onApplied }) {
  const toast = useToast();
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  const runCheck = useCallback(async () => {
    if (!row) return;
    setChecking(true);
    setError("");
    setReport(null);
    try {
      const res = await Axios.post(`/maps/map-files/${row.id}/georeference/sharpen/check`, {}, authHeaders());
      setReport(res.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "The check failed.");
    } finally {
      setChecking(false);
    }
  }, [row]);

  useEffect(() => {
    if (row) runCheck();
    else {
      setReport(null);
      setError("");
    }
  }, [row, runCheck]);

  const apply = async () => {
    setApplying(true);
    try {
      await Axios.post(`/maps/map-files/${row.id}/georeference/sharpen`, {}, authHeaders());
      toast.success("Sharpening started. The sheet shows Processing until its new tiles are ready.");
      onApplied?.();
      onHide();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to start sharpening.");
    } finally {
      setApplying(false);
    }
  };

  const verdict = report ? VERDICTS[report.status] || VERDICTS.error : null;
  const zoomGain =
    report && report.max_zoom_now && report.max_zoom_after ? report.max_zoom_after - report.max_zoom_now : null;

  return (
    <Modal show={!!row} onHide={applying ? undefined : onHide} centered size="md">
      <Modal.Header closeButton={!applying} className="bg-light border-0 py-2.5">
        <Modal.Title className="fs-6 fw-semibold text-primary text-truncate">
          <Sparkles size={17} className="me-1.5 align-middle" />
          Sharpen sheet: <span className="text-muted fw-normal">{row?.title || row?.file_name}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="fs-sm">
        {checking && (
          <div className="d-flex align-items-center gap-2 py-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
            <span>Checking this sheet against its original PDF. This can take up to a minute.</span>
          </div>
        )}

        {!checking && error && <p className="text-danger mb-0">{error}</p>}

        {!checking && report && (
          <>
            <p className={`fw-semibold mb-1 ${verdict.className}`}>{verdict.text}</p>
            {report.reason && <p className="text-muted mb-3">{report.reason}</p>}

            <Table size="sm" bordered className="mb-3 fs-xs">
              <tbody>
                <tr>
                  <th className="fw-medium" style={{ width: "32%" }}>Sheet</th>
                  <td>{GROUP_LABELS[report.group] || report.group || "-"}</td>
                </tr>
                {report.max_zoom_after && (
                  <tr>
                    <th className="fw-medium">Max zoom</th>
                    <td>
                      {report.max_zoom_now ?? "?"} → <strong>{report.max_zoom_after}</strong>
                      {zoomGain !== null && zoomGain <= 0 && <span className="text-muted"> (no gain)</span>}
                    </td>
                  </tr>
                )}
                {report.render_dpi && (
                  <tr>
                    <th className="fw-medium">New render</th>
                    <td>
                      {report.render_dpi} DPI, {fmt(report.render_px?.[0])} × {fmt(report.render_px?.[1])} px
                      {report.limited_by && <span className="text-muted"> (limited by {report.limited_by})</span>}
                    </td>
                  </tr>
                )}
                {report.storage_factor && (
                  <tr>
                    <th className="fw-medium">Tile storage</th>
                    <td>about {report.storage_factor}× the current tiles</td>
                  </tr>
                )}
                {report.match && (
                  <tr>
                    <th className="fw-medium">Crop found</th>
                    <td>
                      at {report.match.dpi} DPI, rotated {report.match.rotation}°, difference {report.match.diff}
                      {report.match.margin ? `, ${report.match.margin}× better than any other place` : ""}
                    </td>
                  </tr>
                )}
                {report.verify && (
                  <tr>
                    <th className="fw-medium">Rebuild check</th>
                    <td>
                      {(report.verify.iou * 100).toFixed(2)}% overlap, difference {report.verify.diff}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {report.status === "ok" && (
              <p className="text-muted mb-0">
                Applying re-processes this sheet in the background, which takes a few minutes. Its control points stay
                as they are, and its current tiles stay until the new ones are ready.
              </p>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-secondary" size="sm" onClick={onHide} disabled={applying}>
          Close
        </Button>
        {!checking && (report || error) && (
          <Button variant="outline-primary" size="sm" onClick={runCheck} disabled={applying}>
            Check again
          </Button>
        )}
        {!checking && report?.status === "ok" && (
          <Button variant="primary" size="sm" onClick={apply} disabled={applying}>
            {applying ? "Starting…" : "Apply"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
